// src/components/shared/IOSInstallGuide.js
// Guide d'installation iOS - 5 étapes réelles avec repères visuels, footer toujours visible

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';

const IOSInstallGuide = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [step, setStep] = useState(1);
  const totalSteps = 5;

  useEffect(() => {
    if (open) setStep(1);
  }, [open]);

  if (!open) return null;

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
    else onClose();
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const steps = [
    { title: t('getApp.ios.step0Title'), text: t('getApp.ios.step0Text'), image: '/onboarding/ios-step0.jpg' },
    { title: t('getApp.ios.step1Title'), text: t('getApp.ios.step1Text'), image: '/onboarding/ios-step1.jpg' },
    { title: t('getApp.ios.step2Title'), text: t('getApp.ios.step2Text'), image: '/onboarding/ios-step2.jpg' },
    { title: t('getApp.ios.step3Title'), text: t('getApp.ios.step3Text'), image: '/onboarding/ios-step3.jpg' },
    { title: t('getApp.ios.step4Title'), text: t('getApp.ios.step4Text'), image: '/onboarding/ios-step4.jpg' },
  ];

  const current = steps[step - 1];
  const isLastStep = step === totalSteps;

  const styles = {
    overlay: {
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      animation: 'fadeInOverlay 200ms ease-out',
    },
    sheet: {
      width: '100%', maxWidth: '480px', backgroundColor: theme.bg,
      borderTopLeftRadius: '24px', borderTopRightRadius: '24px',
      maxHeight: '88vh', display: 'flex', flexDirection: 'column',
      animation: 'slideUpSheet 280ms cubic-bezier(0.32, 0.72, 0, 1)',
      overflow: 'hidden',
    },
    scrollArea: {
      overflowY: 'auto', padding: '10px 20px 16px', flex: 1, minHeight: 0,
    },
    grabber: {
      width: '40px', height: '5px', borderRadius: '3px', backgroundColor: theme.border,
      margin: '6px auto 14px',
    },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' },
    progressText: { fontSize: '12px', fontWeight: '700', color: theme.gold },
    closeBtn: { background: 'none', border: 'none', color: theme.textSecondary, fontSize: '20px', cursor: 'pointer', padding: '4px' },
    progressBarTrack: { height: '4px', backgroundColor: theme.bgInput, borderRadius: '2px', marginBottom: '16px', overflow: 'hidden' },
    progressBarFill: { height: '100%', backgroundColor: theme.gold, borderRadius: '2px', transition: 'width 250ms ease' },
    title: { fontSize: '17px', fontWeight: '700', color: theme.textPrimary, textAlign: 'center', marginBottom: '6px' },
    text: { fontSize: '13px', color: theme.textSecondary, textAlign: 'center', lineHeight: '1.5', marginBottom: '14px' },
    imageWrap: {
      display: 'flex', justifyContent: 'center', backgroundColor: theme.bgCard,
      borderRadius: '16px', border: `1px solid ${theme.border}`, padding: '10px',
    },
    image: {
      width: '100%', maxWidth: '260px', height: 'auto', maxHeight: '280px',
      objectFit: 'contain', borderRadius: '10px',
    },
    finalBox: { textAlign: 'center', padding: '10px 0 4px' },
    finalTitle: { fontSize: '15px', fontWeight: '700', color: theme.gold },
    finalText: { fontSize: '13px', color: theme.textSecondary, marginTop: '4px' },
    footer: {
      display: 'flex', gap: '10px', padding: '14px 20px calc(env(safe-area-inset-bottom, 0px) + 16px)',
      borderTop: `1px solid ${theme.borderSoft}`, backgroundColor: theme.bg, flexShrink: 0,
    },
    backBtn: {
      padding: '13px 20px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`,
      borderRadius: '12px', color: theme.textPrimary, fontSize: '14px', fontWeight: '600', cursor: 'pointer',
    },
    nextBtn: {
      flex: 1, padding: '13px 20px', backgroundColor: theme.gold, border: 'none',
      borderRadius: '12px', color: '#FFFFFF', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <style>{`
        @keyframes fadeInOverlay { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUpSheet { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={styles.scrollArea}>
          <div style={styles.grabber} />
          <div style={styles.headerRow}>
            <span style={styles.progressText}>{step}/{totalSteps}</span>
            <button style={styles.closeBtn} onClick={onClose}>✕</button>
          </div>
          <div style={styles.progressBarTrack}>
            <div style={{ ...styles.progressBarFill, width: `${(step / totalSteps) * 100}%` }} />
          </div>

          <div style={styles.title}>{current.title}</div>
          <div style={styles.text}>{current.text}</div>

          <div style={styles.imageWrap}>
            <img src={current.image} alt={`Étape ${step}`} style={styles.image} />
          </div>

          {isLastStep && (
            <div style={styles.finalBox}>
              <div style={styles.finalTitle}>🎉 {t('getApp.ios.finalTitle')}</div>
              <div style={styles.finalText}>{t('getApp.ios.finalText')}</div>
            </div>
          )}
        </div>

        <div style={styles.footer}>
          {step > 1 && (
            <button style={styles.backBtn} onClick={handleBack}>{t('common.back')}</button>
          )}
          <button style={styles.nextBtn} onClick={handleNext}>
            {isLastStep ? t('common.close') : t('common.next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IOSInstallGuide;